import "./styles.scss";
// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { Preferences } from "@capacitor/preferences";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { useToast } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { OnboardingToStripeICAgreementContainer } from "@src/appV2/redesign/ICAgreement/OnboardingToStripeICAgreementContainer";
import { updateAgentData } from "@src/appV2/redesign/Onboarding/api/updateAgentData";
import { ONBOARDING_STAGES } from "@src/appV2/redesign/Onboarding/util/constants";
import { useSkillsAssessmentFeatureFlags } from "@src/appV2/SkillsAssessment/api/useSkillsAssessmentFeatureFlags";
import { useSkillsAssessments } from "@src/appV2/SkillsAssessment/api/useSkillsAssessments";
import { AssessmentType } from "@src/appV2/SkillsAssessment/types";
import { Worker } from "@src/appV2/Worker/api/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useUpdateWorkerCache } from "@src/appV2/Worker/useUpdateWorkerCache";
import { USER_EVENTS } from "@src/constants";
import { FC, useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import { createPaymentServiceAccount, signedContractorAgreement } from "./api";
import { OnboardingRouterPath } from "../routing/constant/onboardingRoute";
import { LocalStorage, reloadAgentProfile } from "../store/session";

interface OnboardToStripeProps {
  nextStagePath?: () => string;
  disableBackButton?: boolean;
}

export const OnboardToStripe: FC<OnboardToStripeProps> = ({ nextStagePath, disableBackButton }) => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const worker = useDefinedWorker();
  const history = useHistory<{ redirectTo: "" }>();
  const { showErrorToast } = useToast();
  const updateWorkerCache = useUpdateWorkerCache();

  const isNewStripeOnboardingEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_NEW_STRIPE_ONBOARDING, {
    defaultValue: false,
  });

  useEffect(() => {
    const hasWorkerName = !!worker.name?.trim();
    setLoading(!hasWorkerName);
    if (hasWorkerName) {
      return;
    }
    dispatch(reloadAgentProfile());
  }, [dispatch, worker.name]);

  const handleAccountCreate = useCallback(async () => {
    const splitName = worker.name?.split(" ") ?? [];
    let firstName = "";
    let lastName = "";
    if (splitName.length > 0) {
      firstName = splitName[0] ?? "";
      lastName = splitName[splitName?.length - 1] ?? "";
    }

    const hcpAddress = {
      line1: `${worker.address?.streetNumber || ""} ${worker.address?.streetName ?? ""}`,
      city: worker.address?.city,
      state: worker.address?.stateCode,
      postalCode: worker.address?.postalCode,
    };
    return await createPaymentServiceAccount({
      agentId: worker.userId,
      email: worker.email as string,
      msa: worker.address?.metropolitanStatisticalArea,
      individual: {
        firstName: worker.firstName || firstName,
        lastName: worker.lastName || lastName,
        phone: worker.phone,
        birthday: worker.dob,
        address: hcpAddress,
      },
      isOnboardingCompleted: isNewStripeOnboardingEnabled ? false : undefined,
    });
  }, [worker, isNewStripeOnboardingEnabled]);

  const { isSkillsAssessmentOnboardingEnabled, skillsAssessmentConfig } =
    useSkillsAssessmentFeatureFlags();
  const { data: skillAssessments, isSuccess: isSuccessSkillAssessments } = useSkillsAssessments(
    {
      workerId: worker.userId,
      qualificationNames: skillsAssessmentConfig.availableQualificationNames,
    },
    { enabled: isSkillsAssessmentOnboardingEnabled }
  );

  const hasAsynchronousAssessment =
    isSuccessSkillAssessments &&
    skillAssessments.data.length > 0 &&
    skillAssessments.included.some(
      (included) =>
        included.type === "assessment-status" &&
        included.attributes.type === AssessmentType.ASYNCHRONOUS
    );

  const handleAgreementAcceptance = useCallback(
    async (agreementVersion) => {
      try {
        setLoading(true);

        const updatedAgent = await signedContractorAgreement({
          agentId: worker.userId,
          agreementVersion,
          signature: worker.name,
          forceReSignAgreement: false,
        });
        logEvent(USER_EVENTS.SIGNED_CONTRACT, {
          hcpID: worker.userId,
          email: worker.email,
          signature: worker.name,
          agreementVersion,
        });

        const { signedContractorAgreements } = updatedAgent;

        if (!worker.paymentAccountInfo) {
          try {
            const paymentAccountInfo = await handleAccountCreate();
            updateWorkerCache({
              signedContractorAgreements,
              paymentAccountInfo,
            });
          } catch (error) {
            const errorMessage = (error.response as any)?.body?.message;
            // Stripe may indicate error passed client-side validation, like invalid address, postal code, etc.
            const isStripeError = error.status === 400 && errorMessage;
            const defaultMessage =
              "You have accepted the Independent Contractor Agreement successfully. However, we are having a technical issue accessing or creating your Stripe account.  Please contact Customer Support.";
            const stripeErrorMessage = `You have accepted the Independent Contractor Agreement successfully. However, your stripe account wasn't created. Error: ${errorMessage}. Please contact Customer Support.`;
            showErrorToast(isStripeError ? stripeErrorMessage : defaultMessage);
            logError(APP_V2_APP_EVENTS.STRIPE_ONBOARDING_ERROR, {
              error,
              metadata: {
                message: "Error occurred setting up stripe account on IC Agreement signing",
                userId: worker.userId,
              },
            });
            updateWorkerCache({
              signedContractorAgreements,
            });
            return;
          }
        }
        updateWorkerCache({
          signedContractorAgreements,
        });

        const { value } = await Preferences.get({ key: LocalStorage.AGENT });
        if (value) {
          const agent = JSON.parse(value) as Worker;
          agent.signedContractorAgreements = signedContractorAgreements;
          await Preferences.set({
            key: LocalStorage.AGENT,
            value: JSON.stringify(agent),
          });
        }

        await updateAgentData({
          stage: ONBOARDING_STAGES.CONTRACTOR_AGREEMENT,
        });

        if (hasAsynchronousAssessment) {
          history.push(OnboardingRouterPath.ONBOARDING_SKILL_ASSESSMENT);
          return;
        }

        if (nextStagePath) {
          history.push(nextStagePath());
          return;
        }
        const { redirectTo } = history?.location?.state || {};
        if (redirectTo) {
          history.replace(redirectTo);
        }
      } catch (error) {
        showErrorToast(
          "Sorry we are experiencing technical problems. Please contact support, or try again later."
        );
        logError(APP_V2_APP_EVENTS.STRIPE_ONBOARDING_ERROR, {
          error,
          metadata: {
            message: "Error occurred signing IC Agreement",
            userId: worker.userId,
          },
        });
      } finally {
        setLoading(false);
      }
    },
    [
      worker.userId,
      worker.name,
      worker.email,
      worker.paymentAccountInfo,
      updateWorkerCache,
      hasAsynchronousAssessment,
      nextStagePath,
      history,
      handleAccountCreate,
      showErrorToast,
    ]
  );

  return (
    <div className="cbhAppV1">
      <OnboardingToStripeICAgreementContainer
        worker={worker}
        onSignAgreement={handleAgreementAcceptance}
        isSigningAgreement={loading}
      />
    </div>
  );
};
